import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, delay, switchMap, tap, filter } from 'rxjs/operators';
import { UserAuth } from './user';
import { environment } from '@env/environment';
import { LocalStorageService } from '@core/local-storage/local-storage.service';
import { Router, Routes } from '@angular/router';
import { ApiService } from '@core/services/api.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NgxPermissionsService } from 'ngx-permissions';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { TabsSharedDataService } from '@modules/tabs/tabs-shared-data.service';
import { LocalStorageEnum } from '@modules/consulta/produtos/rnpa-pessoas/enumerators';
import { UsuarioService } from '@core/services/usuario.service';
import { PerfilEnum } from '@core/models/perfil.models';

const routes = {
    funcionalidadesUsuarioCrud: `crud/usuario/funcionalidades`,
    mfaValidator: `validate-mfa-token`,
    mfaRequestNewToken:`request-mfa-token`,
    geraCookieToken:`secure`
}



@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private currentUserSubject: BehaviorSubject<UserAuth>;
    public currentUser: Observable<UserAuth>;
    private authToken: string = environment.accessAuthToken;
    public loggedIn = new BehaviorSubject<boolean>(false);

    constructor(private apiService: ApiService,
        private localStorageService: LocalStorageService,
        private router: Router,
        private permissionsService: NgxPermissionsService,
        private modalService: NgbModal,
        private usuarioService: UsuarioService,
        private tabService: TabsSharedDataService) {
        this.currentUserSubject = new BehaviorSubject<UserAuth>(JSON.parse(localStorageService.getItem('currentUser')));
        this.currentUser = this.currentUserSubject.asObservable();
        this.loggedIn = new BehaviorSubject<boolean>(localStorageService.getItem('currentUser') != undefined);
    }

    public get isLoggedIn() {
        return this.loggedIn.asObservable().pipe(delay(0));
    }

    public get currentUserValue(): UserAuth {
        return this.currentUserSubject.value;
    }

    //<-------Login com Bearer Token------->
    loginWithToken(usuario: string, senha: string) {
        let dataInfo = { usuario, senha };
        return this.apiService.post<any>(environment.auth, dataInfo)
            .pipe(map(data => {
                if (data.success) {
                    this.createDadosUsuario(data);
                }
                return data;
            }));
    }

    //<-------Login com Cookie------->
    login(usuario: string, senha: string) {
        let dataInfo = { usuario, senha };
        return this.apiService.post<any>(`${environment.auth}/${routes.geraCookieToken}`, dataInfo)
            .pipe(map(data => {
                const helper = new JwtHelperService();
                const decoded = helper.decodeToken(data.token);
                const idPerfil = decoded?.token.id_perfil;
                if (data.success && decoded.token.id_perfil != PerfilEnum.Api) {
                    this.createDadosUsuario(data);
                } else if(idPerfil == PerfilEnum.Api){
                    data.message = "Usuário ou senha inválidos."
                    data.success = false;
                    return data
                }
                return data;
            }));
    }

    createDadosUsuario(data: any): void {
        let dadosUsuario: any;

        this.usuarioService.getDadosPerfil(data.id_usuario).pipe(
            switchMap((dados: any) => {
                dadosUsuario = dados;

                const dt = moment().add(environment.tempoAcesso, 'minutes').toDate();
                this.localStorageService.setItem('dataExpiracaoAcesso', dt);
                this.localStorageService.setItem('dataExpiracaoToken', dt);
                this.currentUserSubject.next(dadosUsuario);
                this.localStorageService.setItem('currentUser', JSON.stringify(dadosUsuario));

                return this.funcionalidadesUsuario();
            }),
            tap((funcionalidades) => {
                dadosUsuario.funcionalidades = funcionalidades.map(x => x.idFuncionalidade.toString());
                this.localStorageService.setItem('currentUser', JSON.stringify(dadosUsuario));
                this.permissionsService.loadPermissions(dadosUsuario.funcionalidades);
            })
        ).subscribe();
    }

    logout(setReturnUrl?: boolean, message?: string) {
        let tour = document.querySelector('joyride-step')
        tour ? tour.classList.add('d-none') : null

        if (!this.router.url.startsWith('/login')) {
            this.localStorageService.removeItem('currentUser');
            this.localStorageService.removeItem('token');
            localStorage.removeItem('cnseg_storage')
            this.currentUserSubject.next(null);
            this.loggedIn.next(false);
            this.modalService.dismissAll(true);

            if (setReturnUrl)
                this.router.navigate(['/login'], { queryParams: { returnUrl: this.router.url, msg: message } });
            else
                this.router.navigate(['/login'], { queryParams: { msg: message } });
        }
    }


    funcionalidadesUsuario() {
        return this.apiService.getCustomApi<any>(`${environment.lote}/${routes.funcionalidadesUsuarioCrud}`);
    }

    refreshToken() {
        let params = new HttpParams();
        params = params.append('ignoreLoading', 'true');
        params = params.append('ignoreError', 'true');
        return this.apiService.getCustomApi<any>(`${environment.refreshToken}`, params);
    }

    mfaVerification(data) {
        const headers = new HttpHeaders({ 'Authorization': this.authToken });
        return this.apiService.post<any>(routes.mfaValidator, data, headers);
    }

    mfaNewToken(data) {
        const headers = new HttpHeaders({ 'Authorization': this.authToken });
        return this.apiService.post<any>(routes.mfaRequestNewToken, data, headers);
    }
}